import React, { useEffect, useMemo, ReactNode, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Transition } from '@headlessui/react';

import { updateAndReplaceURLSearchParams, locale } from '@trqk/utils';

import { Button, IconX } from '../..';
import { DropdownPreFilter, EarningsPreFilter } from '.';
import { defaultFilterHeight } from '../constants';

interface PreFiltersProps<TData extends Record<string, unknown>> {
    /**
     * First element of array that results from calling `@xstate/react`'s useMachine method with
     * a machine generated by getPreFilterMachine
     */
    current: PreFilterState<TData>;
    /**
     * getSize method returned by useAutoSize; used to get the width of the table and filters
     */
    getSize(): void;
    /**
     * Map of {@link PreFilterConfig} objects for setting up the filters
     */
    preFilterConfigs: Map<string, PreFilterConfig>;
    /**
     * Fully rendered filters; typically used to manage an AppSync filtered query which fetches the
     * list of all items before it is passed to the preFilter machine
     */
    renderedFilters?: ReactNode;
    /**
     * Second element of array that results from calling `@xstate/react`'s useMachine method with
     * a machine generated by getPreFilterMachine
     */
    send: SendPreFilterEvent<TData>;
    /**
     * Method returned by the useEntities hook
     */
    setStateListeners(setState: SetStateOfEntityListeners): void;
    /**
     * Whether the filters should initially be shown
     * @defaultValue false
     */
    showByDefault?: boolean;
    /**
     * A unique name for the table this set of filters is used with. Used for setting the entity
     * machine state listener with `setStateListeners`.
     */
    tableName: string;
    /**
     * Width in pixels
     */
    width: number;
}

/**
 * Renders the filters and the "Clear Filters" button for a table that uses the usePreFilteredList
 * hook (and the underlying preFilter state machine).
 * @param props - see {@link PreFiltersProps}
 */
export function PreFilters<TData extends Record<string, unknown>>({
    current,
    getSize,
    showByDefault = false,
    preFilterConfigs,
    renderedFilters,
    send,
    setStateListeners,
    tableName,
    width,
}: PreFiltersProps<TData>): JSX.Element {
    const history = useHistory();

    const emptyURLSearchParams = useMemo(
        () =>
            Array.from(preFilterConfigs.values()).map(
                ({ urlParamKey }: PreFilterConfig): URLSearchParam => ({
                    key: urlParamKey,
                    value: null,
                }),
            ),
        [],
    );

    const clearFiltersAndRemoveUrlParameters = (): void => {
        updateAndReplaceURLSearchParams(history, emptyURLSearchParams);

        send({ type: 'CLEAR_PRE_FILTER_VALUES' });
    };

    const configs = useMemo((): {
        dropdownFilterConfigs: PreFilterConfig[];
        earningsFilterConfig: {
            max: PreFilterConfig;
            min: PreFilterConfig;
        } | null;
    } => {
        const preFilterConfigsArray = Array.from(preFilterConfigs.values());

        const dropdownFilterConfigs: PreFilterConfig[] = [];
        let earningsMaxConfig: PreFilterConfig | null = null;
        let earningsMinConfig: PreFilterConfig | null = null;

        preFilterConfigsArray.forEach(
            (preFilterConfig: PreFilterConfig): void => {
                if (preFilterConfig.renderType === 'dropdown') {
                    dropdownFilterConfigs.push(preFilterConfig);
                } else if (
                    preFilterConfig.renderType === 'earnings' &&
                    preFilterConfig.earningsType === 'max'
                ) {
                    earningsMaxConfig = preFilterConfig;
                } else if (
                    preFilterConfig.renderType === 'earnings' &&
                    preFilterConfig.earningsType === 'min'
                ) {
                    earningsMinConfig = preFilterConfig;
                }
            },
        );

        const earningsFilterConfig =
            earningsMaxConfig && earningsMinConfig
                ? {
                      max: earningsMaxConfig,
                      min: earningsMinConfig,
                  }
                : null;

        return {
            dropdownFilterConfigs,
            earningsFilterConfig,
        };
    }, [preFilterConfigs]);

    const [showFilters, setShowFilters] = useState(showByDefault);

    useEffect(() => {
        // Show filters if there are any filters set by URL parameters
        if (!showFilters && current.context.preFilterValues.size > 0) {
            setShowFilters(true);
        }
    }, [current.context.preFilterValues.size, showFilters]);

    useEffect(() => {
        setStateListeners(
            (prev: EntityStateListeners): EntityStateListeners => ({
                ...prev,
                [tableName]: (state: EntityState): void => {
                    if (state.event.type === 'SET_SELECTED_ENTITY') {
                        updateAndReplaceURLSearchParams(
                            history,
                            emptyURLSearchParams,
                        );

                        send({
                            type: 'CLEAR_PRE_FILTER_VALUES',
                        });

                        setShowFilters(false);
                    }
                },
            }),
        );
    }, []);

    return (
        <div style={{ width }}>
            <div className="flex justify-end mb-2">
                <Transition
                    show={!showFilters}
                    enter="transition-opacity duration-500"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                >
                    <button
                        className="font-semibold underline text-primary-500"
                        type="button"
                        onClick={() => {
                            setShowFilters(true);
                            // Resize the table after the filters have been expanded. We do this
                            // twice in case the user has low memory available and the transition
                            // takes longer than expected.
                            setTimeout(getSize, 900);
                            setTimeout(getSize, 2000);
                        }}
                    >
                        {
                            locale.enUS.STATEMENTS.PERFORMANCES.BUTTONS
                                .ADVANCED_SEARCH
                        }
                    </button>
                </Transition>
            </div>
            <Transition
                show={showFilters}
                enter="transition-opacity duration-500"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="p-4 border rounded border-tertiary-200">
                    <div className="flex items-start justify-between">
                        <div className="flex flex-wrap gap-1">
                            {/*
                                Any filters that need to be pre-rendered so they can update external
                                state go here.
                            */}
                            {renderedFilters && <>{renderedFilters}</>}
                            {/*
                                Dropdown components whose options are controlled by the preFilter
                                machine. These components send updates to the preFilter machine so
                                the data can be filtered accordingly.
                            */}
                            {configs.dropdownFilterConfigs.map(
                                (
                                    preFilterConfig: PreFilterConfig,
                                ): JSX.Element | null => {
                                    const dropdownInputMachine =
                                        current.context.dropdownInputMachines?.[
                                            preFilterConfig.id
                                        ];

                                    if (!dropdownInputMachine) {
                                        return null;
                                    }

                                    return (
                                        <DropdownPreFilter
                                            key={preFilterConfig.id}
                                            current={current}
                                            dropdownInputMachine={
                                                dropdownInputMachine
                                            }
                                            preFilterConfig={preFilterConfig}
                                            send={send}
                                        />
                                    );
                                },
                            )}
                            {/*
                                Earnings filter that sends updates to the preFilter machine so the
                                data can be filtered accordingly.
                            */}
                            {configs.earningsFilterConfig && (
                                <EarningsPreFilter
                                    current={current}
                                    send={send}
                                    maxFilterId={
                                        configs.earningsFilterConfig.max.id
                                    }
                                    minFilterId={
                                        configs.earningsFilterConfig.min.id
                                    }
                                />
                            )}
                            <div style={{ height: defaultFilterHeight }}>
                                <Button
                                    autoWidth={true}
                                    color="tertiary-light-bg"
                                    addClasses="flex-none px-3 h-full"
                                    handleClick={
                                        clearFiltersAndRemoveUrlParameters
                                    }
                                    padding="sm"
                                >
                                    {
                                        locale.enUS.STATEMENTS.PERFORMANCES
                                            .BUTTONS.CLEAR_FILTERS
                                    }
                                </Button>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="ml-4 font-semibold underline text-quinary-500"
                            onClick={() => {
                                clearFiltersAndRemoveUrlParameters();
                                setShowFilters(false);
                                // Resize the table after the filters have been closed. We do this
                                // twice in case the user has low memory available and the
                                // transition takes longer than expected.
                                setTimeout(getSize, 1000);
                                setTimeout(getSize, 2000);
                            }}
                        >
                            <IconX height={16} fillColor="tertiary-300" />
                        </button>
                    </div>
                </div>
            </Transition>
        </div>
    );
}
